<!--
  Harvesting 2.0 — un APV.

  CE QUI CHANGE
  L'écran était une muraille : trois blocs, puis trois onglets dont le premier
  empilait sept sections de saisie et de lecture mélangées. Il ouvrait sur une
  page BLANCHE pendant le chargement (`v-if="parcel"`), n'avait aucun état
  d'erreur — un enregistrement raté ne disait rien du tout — et supprimait une
  ligne de coût sans confirmation.

  Quatre onglets, séparés par ce qu'on vient y faire :
    Synthèse — 90 % des visites veulent LIRE, pas saisir.
    Fiche    — tout l'éditable. La saisie est un mode, pas une page.
    Avize    — ce qui est sorti, aviz par aviz.
    Coûts    — les frais de parcelle et l'argent déjà engagé.

  Les trois boîtes du haut portaient un `style="border:1px solid rgba(255,255,255,.15)"`
  en dur : invisible sur le thème clair, c'est-à-dire sur tous les écrans réels.
-->
<template>
  <div>
    <!-- Chargement / erreur / contenu : trois branches, chacune avec sa clé. -->
    <div v-if="loading" key="pd-l" class="hv2-loading">
      <vs-progress indeterminate color="primary" class="mb-2" />Chargement de la parcelle…
    </div>

    <div v-else-if="error" key="pd-e">
      <vx-card>
        <div class="hv2-error">
          <b>Cette parcelle n'a pas pu être chargée.</b><br />{{ error }}
        </div>
        <vs-button class="mt-4" size="small" type="border"
                   @click="$router.push({ name: 'harvesting2-parcels' })">
          Retour à la liste
        </vs-button>
      </vx-card>
    </div>

    <div v-else-if="parcel" key="pd-c">
      <!-- ========================== EN-TÊTE ========================== -->
      <vx-card class="mb-base">
        <div class="hv2-sec-head">
          <div>
            <h4 class="hv2-sec-title">{{ parcelName(parcel) }}</h4>
            <p class="hv2-sec-sub">
              <span class="hv2-code">{{ parcel.apv_code }}</span>
              <span v-if="parcel.ocol"> · {{ parcel.ocol }}</span>
              <span v-if="place"> · {{ place }}</span>
            </p>
          </div>
          <!-- Le verdict en tête de fiche : la même phrase que la liste et le
               mail, calculée une seule fois côté serveur. -->
          <div class="hv2-pills">
            <hv-pill :label="STATUS.label(parcel.status)" :tone="STATUS.tone(parcel.status)"
                     :icon="STATUS.icon(parcel.status)" />
            <hv-pill :label="SOURCE.label(parcel.source)" :tone="SOURCE.tone(parcel.source)"
                     :icon="SOURCE.icon(parcel.source)" />
            <hv-pill v-if="parcel.decision" :label="DECISION.label(parcel.decision)"
                     :tone="DECISION.tone(parcel.decision)" :icon="DECISION.icon(parcel.decision)" />
            <hv-pill v-if="parcel.deadline_extended" label="Prolongé" tone="ok" icon="CalendarIcon"
                     :title="extensionNote(parcel)" />
            <hv-pill v-if="parcel.dismissed_at" label="Écarté" tone="danger" icon="EyeOffIcon"
                     :title="parcel.dismiss_reason || ''" />
            <!-- Écarter n'est proposé que sur une parcelle vide. Le serveur
                 revérifie de toute façon : un bouton n'est pas un contrôle. -->
            <vs-button v-if="canDismiss" size="small" type="border" color="danger"
                       icon-pack="feather" icon="icon-eye-off" @click="dismissOpen = true">
              Écarter cet APV
            </vs-button>
            <vs-button v-if="parcel.dismissed_at" size="small" type="border"
                       icon-pack="feather" icon="icon-rotate-ccw" @click="restore">
              Rétablir
            </vs-button>
          </div>
        </div>

        <div v-if="parcel.verdict" class="hv2-verdict mt-3"
             :class="`is-${parcel.verdict.tone}`">{{ parcel.verdict.text }}</div>
        <p v-if="parcel.depot_text" class="hv2-sec-sub" style="margin:6px 0 0">{{ parcel.depot_text }}</p>

        <!-- Chaque volume porte sa source écrite. Une tuile « autorisé » affichait
             ici le volume de la RÉCEPTION sous une étiquette qu'il n'a pas :
             SUMAL ne publie aucun volume autorisé, seulement un stock déclaré. -->
        <div class="hv2-stats mt-4">
          <div class="vx-col w-1/2 lg:w-1/5">
            <hv-stat tier="tile" :value="m3(parcel.reception_m3)" unit="m³" label="réceptionné"
                     :sub="`${int(parcel.receptions)} réception(s)`"
                     :tone="parcel.no_purchase_contract ? 'danger' : ''" />
          </div>
          <div class="vx-col w-1/2 lg:w-1/5">
            <hv-stat tier="tile" :value="m3(parcel.sumal_stock_m3)" unit="m³" label="APV (source SUMAL)"
                     :sub="parcel.sumal_stock_lines
                       ? `stock déclaré, ${int(parcel.sumal_stock_lines)} ligne(s)`
                       : 'rien de déclaré au dépôt'" />
          </div>
          <div class="vx-col w-1/2 lg:w-1/5">
            <hv-stat tier="tile" :value="m3(parcel.m3_out)" unit="m³" label="sorti (production)"
                     :sub="`${int(parcel.avize)} avize`" />
          </div>
          <div class="vx-col w-1/2 lg:w-1/5">
            <hv-stat tier="tile" :value="m3(parcel.remaining_m3)" unit="m³" label="reste en forêt"
                     :sub="Number(parcel.sumal_adjustments) > 0
                       ? `sur ${m3(parcel.cubage_volume_m3)} m³ (contrat ${signedM3(parcel.sumal_adjust_m3)} SUMAL)`
                       : ''" />
          </div>
          <div class="vx-col w-full lg:w-1/5">
            <hv-stat tier="tile" :value="deadlineValue" :label="deadlineLabel"
                     :tone="deadlineToneOf"
                     :sub="parcel.deadline_extended ? extensionNote(parcel) : date(parcel.deadline)" />
          </div>
        </div>

        <!-- La densité énonce elle-même sa couverture : elle ne peut pas se
             faire passer pour une mesure complète. -->
        <div v-if="parcel.density_kg_m3" class="hv2-stats mt-2">
          <div class="vx-col w-full lg:w-2/5">
            <hv-stat tier="tile" :value="int(parcel.density_kg_m3)" unit="kg/m³" label="densité"
                     :sub="`sur ${int(parcel.avize_weighed)} aviz pesé(s) sur ${int(parcel.avize)}`" />
          </div>
        </div>

        <!-- Une jauge qui SAIT dépasser 100 % : la borner afficherait « plein »
             sur une parcelle sur-extraite, ce qui est précisément l'anomalie. -->
        <div v-if="parcel.extraction_rate !== null" class="mt-4">
          <div class="hv2-gauge" :class="{ 'is-over': parcel.extraction_rate > 1 }">
            <div class="hv2-gauge-fill" :style="{ width: Math.min(parcel.extraction_rate * 100, 100) + '%' }"></div>
          </div>
          <!-- Dire par quoi on divise. La jauge annonçait « du volume autorisé »
               alors qu'elle divise par la réception dès que rien n'a été saisi
               à la fiche de martelage, c'est-à-dire presque toujours. -->
          <div class="hv2-gauge-label">
            {{ pct(parcel.extraction_rate) }}
            {{ parcel.cubage_source === 'typed' ? 'du volume autorisé' : 'du volume réceptionné' }} est sorti
            <span v-if="parcel.extraction_rate > 1">— plus que ce que la réception a acheté, à vérifier.</span>
          </div>
        </div>

        <vs-alert v-if="parcel.no_purchase_contract" color="danger" :active="true" class="mt-4">
          Aucune réception pour cet APV. {{ m3(parcel.m3_out) }} m³ sont sortis de forêt sans prix,
          sans garantie et sans échéancier.
          <!-- Le bouton « Encode » du mail : Claude lit le contrat et prépare la
               réception (Chady, 2026-10-27). -->
          <hv-encode-button :apv="parcel.apv_code" class="ml-4" />
        </vs-alert>

        <vs-alert v-if="parcel.not_encoded" color="warning" :active="true" class="mt-4">
          Cette parcelle est connue de SUMAL et n'a aucun produit dans XYLO. Tant que la réception
          n'est pas créée, aucun volume ni aucun coût ne peut lui être rattaché.
          <hv-encode-button v-if="!parcel.no_purchase_contract" :apv="parcel.apv_code" class="ml-4" />
        </vs-alert>
      </vx-card>

      <vs-tabs>
        <!-- ========================= SYNTHÈSE ========================= -->
        <vs-tab label="Synthèse" icon-pack="feather" icon="icon-eye">
          <vx-card class="mt-4">
            <hv-section
              title="Le résultat de la parcelle"
              sub="Le coût d'exploitation est engagé à l'ouverture du chantier : il se calcule sur le volume de la fiche de martelage, pas sur ce qui est réellement sorti.">
              <hv-empty v-if="!result" icon="BarChart2Icon"
                        text="Le résultat n'est pas calculable : il manque au moins un tarif ou le volume autorisé. Complétez la fiche." />
              <div v-else>
                <div class="hv2-kv" v-for="(l, i) in resultLines" :key="i">
                  <div class="flex items-center justify-between">
                    <span class="hv2-kv-k" style="text-transform:none;letter-spacing:0">{{ l.label }}</span>
                    <span class="hv2-kv-v" style="margin-top:0">{{ l.value }}</span>
                  </div>
                </div>

                <p v-if="result.runs && result.runs.estimated" class="hv2-sec-sub mt-3">
                  <span class="hv2-prov">chiffre provisoire</span>
                  {{ result.runs.weighed_runs }} course(s) pesée(s) sur {{ result.runs.runs }} :
                  les autres sont estimées par plaque et par jour, ce qui fusionne deux rotations du
                  même camion. Les courses sont donc sous-comptées et le coût de transport est un plancher.
                </p>

                <vs-alert v-if="result.loss_note" color="warning" :active="true" class="mt-4">
                  <b>Note de perte.</b> {{ m3(result.loss_note.volume_m3) }} m³ achetés et jamais sortis,
                  soit {{ ron(result.loss_note.amount_ron) }} RON d'achat et d'exploitation engagés en pure perte.
                </vs-alert>
              </div>
            </hv-section>
          </vx-card>

          <vx-card class="mt-base">
            <hv-section title="Ce que SUMAL déclare"
                        sub="Faits repris automatiquement de la fiche dépôt, trois fois par jour à 6 h, 12 h et 18 h, heure de Roumanie. Les horodatages de synchro ci-dessous sont dans ce même fuseau.">
              <div class="vx-row">
                <div class="vx-col md:w-1/3 w-full" v-for="kv in sumalFacts" :key="kv.k">
                  <div class="hv2-kv">
                    <div class="hv2-kv-k">{{ kv.k }}</div>
                    <div class="hv2-kv-v" :class="{ 'is-empty': kv.empty }">{{ kv.v }}</div>
                  </div>
                </div>
              </div>

              <!-- Le stock déclaré, ligne par ligne. Il était réduit à « 57,6 m³
                   sur 13 lignes » alors que les 13 lignes sont en base depuis la
                   migration 046 : c'est le seul endroit où l'on voit ce que
                   SUMAL pense qu'il y a sur la parcelle, essence par essence.
                   Tableau posé et non popup — le but est de le voir sans clic. -->
              <vs-divider />
              <h5 class="mb-1">Ce que SUMAL dit qu'il y a au dépôt</h5>
              <p class="hv2-sec-sub mb-4">
                Affiché, jamais géré : aucun document XYLO n'en découle et rien n'est
                réécrit vers SUMAL.
              </p>
              <hv-table :rows="stockRows" :loading="stockLoading" row-key="species"
                        empty-icon="LayersIcon"
                        empty-text="SUMAL ne déclare aucun stock sur ce dépôt.">
                <template slot="thead">
                  <vs-th>Groupe d'espèces</vs-th><vs-th>Espèce</vs-th>
                  <vs-th>Sortiment</vs-th><vs-th>Sous-sortiment</vs-th><vs-th class="hv2-col-n">m³</vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td>{{ r.species_group || DASH }}</vs-td>
                  <vs-td>{{ r.species || DASH }}</vs-td>
                  <vs-td>{{ r.assortment || DASH }}</vs-td>
                  <vs-td>{{ r.sub_assortment || DASH }}</vs-td>
                  <vs-td class="hv2-col-n">{{ m3(r.volume_m3) }}</vs-td>
                </template>
              </hv-table>
              <p v-if="stockRows.length" class="mt-3">
                <b>Total : {{ m3(stockTotal) }} m³</b> sur {{ stockRows.length }} ligne(s).
              </p>
            </hv-section>
          </vx-card>
        </vs-tab>

        <!-- =========================== FICHE =========================== -->
        <vs-tab label="Fiche" icon-pack="feather" icon="icon-edit">
          <vx-card class="mt-4">
            <hv-section
              title="Ce que XYLO décide"
              sub="Le prix, le contrat et les tarifs sont à nous : SUMAL n'en sait rien. Tout ce qui est ici alimente le calcul de résultat.">
              <div class="vx-row">
                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-input class="w-full" label="Nom de la parcelle" v-model="form.display_name"
                            :placeholder="parcel.sumal_label || ''" />
                </div>
                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-input class="w-full" label="Ocol" v-model="form.ocol" />
                </div>
                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-input class="w-full" type="number" label="Volume autorisé (m³) — fiche de martelage"
                            v-model.number="form.cubage_volume_m3" />
                  <span v-if="parcel.cubage_source === 'contract'" class="hv2-sec-sub">
                    Vide ici, donc les {{ m3(parcel.reception_m3) }} m³ de la réception font foi.
                    Ne saisir que si la fiche de martelage dit autre chose.
                  </span>
                  <!-- Les corrections SUMAL s'AJOUTENT au contrat, elles n'écrasent rien
                       (décision du 2026-10-14) : dit ici pour que personne ne retape le
                       total corrigé dans ce champ, ce qui le compterait deux fois. -->
                  <span v-if="Number(parcel.sumal_adjustments) > 0" class="hv2-sec-sub">
                    SUMAL a corrigé ce volume de {{ signedM3(parcel.sumal_adjust_m3) }} m³
                    ({{ int(parcel.sumal_adjustments) }} opération(s), onglet Avize) : volume effectif
                    {{ m3(parcel.cubage_volume_m3) }} m³. Les corrections s'ajoutent au contrat,
                    ne pas les reporter ici.
                  </span>
                </div>

                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-select v-model="form.operating_mode" label="Contrat d'exploitation" class="w-full">
                    <vs-select-item value="" text="—" />
                    <vs-select-item value="own" text="Régie XYLO" />
                    <vs-select-item value="contractor" text="Prestataire" />
                  </vs-select>
                </div>
                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-select v-model="form.contractor_idperson" label="Prestataire" class="w-full"
                             autocomplete :disabled="form.operating_mode !== 'contractor'">
                    <vs-select-item :value="null" text="—" />
                    <vs-select-item v-for="p in people" :key="p.id" :value="p.id" :text="p.name" />
                  </vs-select>
                </div>
                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-input class="w-full" type="number" label="Tarif d'exploitation (RON/m³)"
                            v-model.number="form.logging_rate_ron_m3" />
                  <span class="hv2-sec-sub" v-if="settings.defaults">
                    Négocié entre {{ settings.defaults.logging_ron_m3.min }} et
                    {{ settings.defaults.logging_ron_m3.max }} RON/m³.
                  </span>
                </div>

                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-input class="w-full" type="number" label="Prix d'achat (RON/m³)"
                            v-model.number="form.purchase_price_ron_m3" />
                  <span v-if="parcel.reception_price_ron_m3" class="hv2-sec-sub">
                    La réception porte {{ rate(parcel.reception_price_ron_m3) }} RON/m³.
                  </span>
                </div>
                <div class="vx-col md:w-1/3 w-full mb-4">
                  <vs-input class="w-full" type="number" label="Tarif transport (RON/course)"
                            v-model.number="form.transport_rate_ron_run" />
                </div>
              </div>

              <vs-divider />

              <h5 class="mb-1">Les dates du dépôt</h5>
              <p class="hv2-sec-sub mb-4">
                Ouverture, échéance, clôture et statut sont repris de SUMAL automatiquement
                <span v-if="parcel.sumal_synced_at">(dernière synchro : {{ dateTimeRo(parcel.sumal_synced_at) }})</span>.
                Une correction saisie ici ne tiendra que jusqu'à la synchro suivante.
              </p>
              <div class="vx-row">
                <div class="vx-col md:w-1/4 w-full mb-4">
                  <vs-input class="w-full" type="date" label="Ouverture du dépôt" v-model="form.date_opened" />
                </div>
                <div class="vx-col md:w-1/4 w-full mb-4">
                  <vs-input class="w-full" type="date" label="Échéance de l'autorisation"
                            v-model="form.date_permit_expiry" />
                </div>
                <div class="vx-col md:w-1/4 w-full mb-4">
                  <vs-input class="w-full" type="date" label="Clôture du dépôt" v-model="form.date_closed" />
                </div>
                <div class="vx-col md:w-1/4 w-full mb-4">
                  <vs-select v-model="form.sumal_depot_status" label="Statut du dépôt" class="w-full">
                    <vs-select-item value="ACTIVE" text="Actif" />
                    <vs-select-item value="INACTIVE" text="Inactif" />
                    <vs-select-item value="SUSPENDED" text="Suspendu" />
                  </vs-select>
                </div>
              </div>

              <template v-if="showDecision">
                <vs-divider />
                <h5 class="mb-1">Décision sur l'échéance</h5>
                <p class="hv2-sec-sub mb-4">
                  À moins de {{ parcel.days_to_expiry }} jours, il reste {{ m3(parcel.remaining_m3) }} m³.
                  Enregistrer une décision fait sortir la parcelle des alertes rouges : l'alerte a été
                  répondue, elle doit cesser de crier.
                </p>
                <div class="vx-row">
                  <div class="vx-col md:w-1/3 w-full mb-4">
                    <vs-select v-model="form.decision" label="Décision" class="w-full">
                      <vs-select-item value="" text="— aucune —" />
                      <vs-select-item value="extract" text="Sortir le bois" />
                      <vs-select-item value="request_extension" text="Demander une prolongation" />
                      <vs-select-item value="write_off" text="Acter la perte" />
                    </vs-select>
                  </div>
                  <div class="vx-col md:w-2/3 w-full mb-4">
                    <vs-input class="w-full" label="Commentaire" v-model="form.decision_note" />
                  </div>
                </div>
              </template>

              <vs-divider />
              <vs-textarea label="Notes" v-model="form.notes" />

              <div class="flex items-center mt-2">
                <vs-button :disabled="saving" @click="save">
                  {{ saving ? 'Enregistrement…' : 'Enregistrer' }}
                </vs-button>
                <span v-if="parcel.sheet_gaps && parcel.sheet_gaps.length" class="ml-4 hv2-sec-sub">
                  Manque encore : {{ parcel.sheet_gaps.map(g => GAP.label(g)).join(', ') }}.
                </span>
                <!-- « Dernière écriture » et non « modification » : la synchro SUMAL
                     touche cette ligne toutes les nuits, ce n'est pas forcément
                     quelqu'un qui a saisi quelque chose. -->
                <span v-if="parcel.sheet_updated_at" class="ml-4 hv2-muted">
                  Dernière écriture : {{ dateTime(parcel.sheet_updated_at) }}
                </span>
              </div>
            </hv-section>
          </vx-card>
        </vs-tab>

        <!-- =========================== AVIZE =========================== -->
        <vs-tab label="Avize" icon-pack="feather" icon="icon-truck">
          <vx-card class="mt-4">
            <hv-section
              title="Ce qui est sorti"
              :sub="`${int(parcel.avize)} avize pour ${m3(parcel.m3_out)} m³, du ${date(parcel.first_aviz)} au ${date(parcel.last_aviz)}.`">
              <hv-table :rows="avize" row-key="idoperation" pagination :max-items="20"
                        empty-icon="TruckIcon"
                        empty-text="Aucun aviz sur cette parcelle pour le moment.">
                <template slot="thead">
                  <vs-th>Date</vs-th><vs-th>Aviz</vs-th><vs-th>Plaque</vs-th>
                  <vs-th class="hv2-col-n">m³</vs-th><vs-th>Pesée</vs-th>
                  <vs-th class="hv2-col-n">Tonnes</vs-th><vs-th class="hv2-col-n">Densité kg/m³</vs-th>
                  <vs-th>Production</vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td>{{ date(r.date) }}</vs-td>
                  <vs-td class="hv2-code">{{ r.aviz || DASH }}</vs-td>
                  <vs-td>{{ r.plates || DASH }}</vs-td>
                  <vs-td class="hv2-col-n">{{ m3(r.m3) }}</vs-td>
                  <!-- Quelles lignes nourrissent la densité de la parcelle. La
                       requête renvoyait déjà ce lien sans que rien ne l'affiche. -->
                  <vs-td>
                    <hv-pill v-if="Number(r.weighings) > 0" label="pesé" tone="ok"
                             icon="CheckIcon" :title="date(r.weighed_at)" />
                    <span v-else class="hv2-muted">{{ DASH }}</span>
                  </vs-td>
                  <vs-td class="hv2-col-n">{{ tonnes(r.tonnes) }}</vs-td>
                  <!-- Calculée par l'API (getParcel) : aviz pesé seulement, et
                       « à vérifier » hors plage, comme au tableau de bord. -->
                  <vs-td class="hv2-col-n">
                    <hv-pill v-if="r.density_suspect" tone="warn" :label="`${int(r.density_kg_m3)} · à vérifier`" />
                    <template v-else-if="r.density_kg_m3">{{ int(r.density_kg_m3) }}</template>
                    <span v-else class="hv2-muted">{{ DASH }}</span>
                  </vs-td>
                  <vs-td>
                    <a :href="`/web/production/${r.idoperation}`">{{ r.idoperation }}</a>
                  </vs-td>
                </template>
              </hv-table>
              <p class="hv2-sec-sub mt-3">
                La densité d'un aviz ne se calcule que s'il est relié à une pesée du pont-bascule.
                Celle de la parcelle, en tête d'écran, porte sur les seuls avize pesés ci-dessus et
                dit sur combien elle porte.
              </p>
            </hv-section>

            <!-- Ce que SUMAL a corrigé sur le volume de l'APV sans aviz (migration
                 088, lu par SumalX2n) : la reprimire radie ce que l'APV promettait et
                 que la forêt n'a pas donné (négatif), le prejudiciu est du bois EN
                 PLUS de l'APV, arbres abîmés ou abattus pour une route (positif).
                 Le volume effectif en tête d'écran les inclut déjà. -->
            <hv-section
              v-if="adjustments.length"
              class="mt-6"
              title="Ce que SUMAL a corrigé"
              :sub="`${adjustments.length} opération(s) sans aviz dans le registre SUMAL ; volume effectif ${m3(parcel.cubage_volume_m3)} m³ = contrat ${m3(parcel.cubage_contract_m3)} ${signedM3(parcel.sumal_adjust_m3)}.`">
              <hv-table :rows="adjustments" row-key="id_rulaj" pagination :max-items="10">
                <template slot="thead">
                  <vs-th>Date PV</vs-th><vs-th>Opération SUMAL</vs-th><vs-th>PV</vs-th>
                  <vs-th>Espèces</vs-th><vs-th class="hv2-col-n">m³ pour l'APV</vs-th><vs-th>Statut</vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td>{{ date(r.pv_day) }}<span v-if="r.pv_time" class="hv2-muted"> {{ r.pv_time }}</span></vs-td>
                  <vs-td>{{ r.op_type || DASH }}</vs-td>
                  <vs-td>{{ r.numar_pv || DASH }}</vs-td>
                  <vs-td class="hv2-muted">{{ r.species || DASH }}</vs-td>
                  <vs-td class="hv2-col-n" :class="{ 'hv2-muted': r.status !== 'INREGISTRAT' }">
                    <s v-if="r.status !== 'INREGISTRAT'">{{ signedM3(r.volume_m3) }}</s>
                    <b v-else>{{ signedM3(r.volume_m3) }}</b>
                  </vs-td>
                  <vs-td>
                    <hv-pill v-if="r.status === 'INREGISTRAT'" label="enregistré" tone="ok" />
                    <hv-pill v-else :label="String(r.status || '').replace('_', ' ').toLowerCase()" tone="warn" />
                  </vs-td>
                </template>
              </hv-table>
            </hv-section>
          </vx-card>
        </vs-tab>

        <!-- =========================== COÛTS =========================== -->
        <vs-tab label="Coûts" icon-pack="feather" icon="icon-dollar-sign">
          <vx-card class="mt-4">
            <hv-section
              title="Les autres coûts de la parcelle"
              sub="Création de route, transport de machines, taxes, arrangements, amendes. Tout ce qui ne se déduit ni du prix d'achat ni du tarif d'exploitation.">
              <hv-table :rows="costs" row-key="id" empty-icon="FileTextIcon"
                        empty-text="Aucun frais enregistré sur cette parcelle.">
                <template slot="thead">
                  <vs-th>Type</vs-th><vs-th>Date</vs-th><vs-th class="hv2-col-n">Montant</vs-th>
                  <vs-th>Description</vs-th><vs-th></vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td>{{ COST_TYPE.label(r.cost_type) }}</vs-td>
                  <vs-td>{{ date(r.cost_date) }}</vs-td>
                  <vs-td class="hv2-col-n">{{ num(r.amount) }} {{ r.currency }}</vs-td>
                  <vs-td>{{ r.description || DASH }}</vs-td>
                  <vs-td>
                    <vs-button size="small" type="flat" color="danger" icon-pack="feather"
                               icon="icon-trash-2" @click="confirmRemoveCost(r)" />
                  </vs-td>
                </template>
              </hv-table>

              <vs-divider />
              <div class="vx-row items-end">
                <div class="vx-col md:w-1/4 w-full mb-4">
                  <vs-select v-model="newCost.cost_type" label="Type" class="w-full">
                    <vs-select-item v-for="t in COST_TYPE.items" :key="t.value"
                                    :value="t.value" :text="t.text" />
                  </vs-select>
                </div>
                <div class="vx-col md:w-1/6 w-full mb-4">
                  <vs-input class="w-full" type="number" label="Montant" v-model.number="newCost.amount" />
                </div>
                <div class="vx-col md:w-1/6 w-full mb-4">
                  <vs-select v-model="newCost.currency" label="Devise" class="w-full">
                    <vs-select-item value="RON" text="RON" />
                    <vs-select-item value="EUR" text="EUR" />
                  </vs-select>
                </div>
                <div class="vx-col md:w-1/6 w-full mb-4">
                  <vs-input class="w-full" type="date" label="Date" v-model="newCost.cost_date" />
                </div>
                <div class="vx-col md:w-1/4 w-full mb-4">
                  <vs-input class="w-full" label="Description" v-model="newCost.description" />
                </div>
                <div class="vx-col w-full mb-4">
                  <vs-button size="small" :disabled="!newCost.amount" @click="addCost">
                    Ajouter ce frais
                  </vs-button>
                </div>
              </div>
            </hv-section>
          </vx-card>

          <vx-card class="mt-base">
            <hv-section
              title="Garantie, dépozit et tranches"
              sub="Lu depuis la réception et les mouvements bancaires. Modifiable là-bas, pas ici : ces montants appartiennent au document d'achat.">
              <hv-table :rows="money" row-key="type" empty-icon="CreditCardIcon"
                        empty-text="Aucun mouvement rattaché à la réception de cette parcelle.">
                <template slot="thead">
                  <vs-th>Type</vs-th><vs-th class="hv2-col-n">Montant</vs-th><vs-th class="hv2-col-n">Écritures</vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td>{{ r.type }}</vs-td>
                  <vs-td class="hv2-col-n">{{ ron(r.amount) }}</vs-td>
                  <vs-td class="hv2-col-n">{{ int(r.entries) }}</vs-td>
                </template>
              </hv-table>
            </hv-section>
          </vx-card>
        </vs-tab>
      </vs-tabs>
    </div>

    <!-- Toujours montée, jamais en v-if : Vuesax déplace la popup dans
         document.body, et une popup conditionnelle n'y survit pas.
         Popup et non $vs.dialog type:'prompt' : le prompt programmatique de
         Vuesax n'affiche AUCUN champ de saisie et rappelle accept() sans
         argument — le motif serait toujours vide. -->
    <vs-popup title="Écarter cet APV" :active.sync="dismissOpen">
      <div v-if="parcel">
        <p class="hv2-sec-sub mb-4">
          <b>{{ parcel.apv_code }}</b> — {{ parcelName(parcel) }}.
          Il sortira de la liste des APV, du tableau de bord, du tampon à encoder et du
          rapport du matin. <b>Rien n'est supprimé</b> : la fiche reste accessible par son
          lien, et la synchro SUMAL ne le fera pas revenir. Rétablissable à tout
          moment depuis le segment « Écartés ».
        </p>
        <vs-input class="w-full mb-4" label="Motif"
                  placeholder="Par exemple : code saisi à 14 chiffres, dépôt de test, doublon"
                  v-model="dismissReason" />
        <vs-button color="danger" :disabled="dismissing" @click="dismiss">
          {{ dismissing ? 'En cours…' : 'Écarter cet APV' }}
        </vs-button>
        <vs-button type="border" class="ml-2" :disabled="dismissing"
                   @click="dismissOpen = false">Annuler</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import axios from '@/axios.js'
import formatMixin from './formatMixin'
import fmt from './format'
import HvSection from './components/HvSection'
import HvStat from './components/HvStat'
import HvPill from './components/HvPill'
import HvTable from './components/HvTable'
import HvEmpty from './components/HvEmpty'
import HvEncodeButton from './components/HvEncodeButton'

export default {
  mixins: [formatMixin],
  components: { HvSection, HvStat, HvPill, HvTable, HvEmpty, HvEncodeButton },
  data () {
    return {
      loading: true,
      error: '',
      saving: false,
      parcel: null,
      avize: [],
      // Les corrections de volume que SUMAL porte sur l'APV (migration 088).
      adjustments: [],
      costs: [],
      money: [],
      result: null,
      people: [],
      settings: {},
      stockRows: [],
      stockLoading: true,
      dismissOpen: false,
      dismissing: false,
      dismissReason: '',
      form: {},
      newCost: { cost_type: 'road_building', amount: null, currency: 'RON', cost_date: null, description: '' }
    }
  },
  computed: {
    apv () { return this.$route.params.apv },
    // Județ / Localité, composés une seule fois : le sous-titre de l'en-tête et
    // la fiche SUMAL doivent dire la même chose.
    place () {
      return [this.parcel.sumal_county, this.parcel.sumal_locality].filter(Boolean).join(' / ')
    },
    // On ne propose d'écarter qu'une parcelle qui ne porte aucun document. Le
    // serveur refuse de toute façon en 409 : c'est lui qui fait foi.
    canDismiss () {
      const p = this.parcel
      return !p.dismissed_at && !Number(p.avize) && !Number(p.receptions)
    },
    stockTotal () { return this.stockRows.reduce((s, r) => s + Number(r.volume_m3 || 0), 0) },
    deadlineToneOf () {
      const t = this.deadlineTone(this.parcel.days_to_expiry, this.parcel.permit_is_permanent)
      return t === 'past' || t === 'urgent' ? 'danger' : (t === 'soon' ? 'warn' : '')
    },
    deadlineValue () {
      const p = this.parcel
      if (p.permit_is_permanent) return 'permanent'
      if (p.days_to_expiry === null) return this.DASH
      return String(Math.abs(p.days_to_expiry))
    },
    deadlineLabel () {
      const p = this.parcel
      if (p.permit_is_permanent || p.days_to_expiry === null) return 'échéance'
      return p.days_to_expiry < 0 ? "jours depuis l'échéance" : "jours avant l'échéance"
    },
    // La décision ne se propose que lorsqu'il y a une échéance à arbitrer :
    // l'offrir partout la banaliserait.
    showDecision () {
      const p = this.parcel
      return !!p.decision || (p.status !== 'done' && p.days_to_expiry !== null && p.days_to_expiry <= 60)
    },
    sumalFacts () {
      const p = this.parcel
      const f = (k, v) => ({ k, v: v || this.DASH, empty: !v })
      return [
        f('Dépôt SUMAL', p.sumal_label),
        f('Statut du dépôt', p.in_sumal ? fmt.DEPOT_STATUS.label(p.sumal_depot_status) : null),
        f('Type de dépôt', p.sumal_depot_type),
        f('Ouverture', this.date(p.date_opened) === this.DASH ? null : this.date(p.date_opened)),
        f("Échéance de l'autorisation", p.permit_is_permanent ? 'permanent' : (this.date(p.date_permit_expiry) === this.DASH ? null : this.date(p.date_permit_expiry))),
        // « Clôture » quand elle est passée, « démantèlement prévu » quand elle
        // est à venir : c'est la même colonne SUMAL, mais pas le même fait.
        f(p.deadline_extended ? 'Démantèlement du dépôt (prévu)' : 'Clôture',
          this.date(p.date_closed) === this.DASH ? null : this.date(p.date_closed)),
        f('Județ / Localité', this.place),
        // « Stock déclaré » ne figure plus ici : le tableau ci-dessous le donne
        // ligne par ligne avec son total. Le répéter en résumé au-dessus du
        // détail n'ajoutait rien.
        f('Dernière synchro', p.sumal_synced_at ? this.dateTimeRo(p.sumal_synced_at) : null)
      ]
    },
    resultLines () {
      const r = this.result
      if (!r) return []
      // Un tarif absent affiche un tiret, jamais un zéro : compter un coût
      // inconnu à zéro sous-estimerait le coût au m³.
      const money = (v) => (v === null || v === undefined ? this.DASH : `${this.ron(v)} RON`)
      return [
        { label: 'Achat du bois (volume autorisé × prix)', value: money(r.lines.purchase_ron) },
        { label: 'Exploitation (volume autorisé × tarif)', value: money(r.lines.logging_ron) },
        { label: `Transport (${r.runs.runs} course(s) × tarif)`, value: money(r.lines.transport_ron) },
        { label: 'Autres coûts', value: money(r.lines.other_ron) },
        { label: 'Total', value: money(r.lines.total_ron) },
        { label: 'Coût du m³ rendu usine', value: r.cost_per_m3_eur === null ? this.DASH : `${this.eur(r.cost_per_m3_eur)} EUR` },
        { label: 'm³ par course', value: r.m3_per_run === null ? this.DASH : this.num(r.m3_per_run, 1) }
      ]
    }
  },
  methods: {
    /** « +30,4 » / « −109,9 » : une correction SUMAL porte toujours son signe. */
    signedM3 (v) {
      const n = Number(v) || 0
      return `${n < 0 ? '−' : '+'}${this.m3(Math.abs(n))}`
    },
    notify (title, text, color) {
      this.$vs.notify({
        title, text, color,
        iconPack: 'feather',
        icon: color === 'danger' ? 'icon-alert-circle' : 'icon-check',
        position: 'bottom-right', time: 6000
      })
    },
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`/harvesting2/parcel/${this.apv}`)
        this.parcel = data.parcel
        this.avize = data.avize || []
        this.adjustments = data.adjustments || []
        this.costs = data.costs || []
        this.money = data.money || []
        this.form = {
          display_name: data.parcel.display_name,
          ocol: data.parcel.ocol,
          // Le champ n'est prérempli que si la valeur a été SAISIE : la reprendre
          // depuis la réception la figerait par un simple enregistrement.
          cubage_volume_m3: data.parcel.cubage_source === 'typed' ? data.parcel.cubage_volume_m3 : null,
          purchase_price_ron_m3: data.parcel.purchase_price_ron_m3,
          operating_mode: data.parcel.operating_mode,
          contractor_idperson: data.parcel.contractor_idperson,
          logging_rate_ron_m3: data.parcel.logging_rate_ron_m3,
          transport_rate_ron_run: data.parcel.transport_rate_ron_run,
          date_opened: fmt.dateInput(data.parcel.date_opened),
          date_permit_expiry: fmt.dateInput(data.parcel.date_permit_expiry),
          date_closed: fmt.dateInput(data.parcel.date_closed),
          sumal_depot_status: data.parcel.sumal_depot_status,
          decision: data.parcel.decision,
          decision_note: data.parcel.decision_note,
          notes: data.parcel.notes
        }
        // Le résultat n'est pas bloquant : une fiche incomplète le rend
        // incalculable, ce n'est pas une erreur d'écran.
        try {
          const res = await axios.get(`/harvesting2/result/${this.apv}`)
          this.result = res.data && res.data.complete !== false ? res.data : res.data
        } catch (e) { this.result = null }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Erreur inconnue'
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.saving = true
      try {
        await axios.post(`/harvesting2/parcel/${this.apv}`, this.form)
        this.notify('Fiche enregistrée', 'Les modifications ont été prises en compte.', 'success')
        await this.load()
      } catch (e) {
        this.notify('Enregistrement impossible',
          (e.response && e.response.data && e.response.data.message) || e.message, 'danger')
      } finally {
        this.saving = false
      }
    },
    async addCost () {
      if (!this.newCost.amount) return
      try {
        await axios.post(`/harvesting2/parcel/${this.apv}/cost`, this.newCost)
        this.newCost = { cost_type: 'road_building', amount: null, currency: 'RON', cost_date: null, description: '' }
        await this.load()
      } catch (e) {
        this.notify('Ajout impossible',
          (e.response && e.response.data && e.response.data.message) || e.message, 'danger')
      }
    },
    // Un clic malheureux sur une icône poubelle détruisait la ligne sans un mot.
    confirmRemoveCost (c) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Supprimer ce frais ?',
        text: `${fmt.COST_TYPE.label(c.cost_type)} — ${this.num(c.amount)} ${c.currency}. Cette suppression est définitive.`,
        acceptText: 'Supprimer',
        cancelText: 'Annuler',
        accept: () => this.removeCost(c)
      })
    },
    // Le stock déclaré, chargé à part : rien ne doit empêcher la fiche de
    // s'afficher si SUMAL n'a rien à dire sur ce dépôt.
    loadStock () {
      this.stockLoading = true
      axios.get(`/sumal2/stock/${this.apv}`)
        .then((r) => {
          // Trié par volume : la ligne qui pèse se lit en premier. HvTable
          // suffixe déjà la clé par l'index, donc les doublons d'espèce ne
          // fabriquent pas de clé en double.
          this.stockRows = (r.data || [])
            .slice()
            .sort((a, b) => Number(b.volume_m3 || 0) - Number(a.volume_m3 || 0))
        })
        .catch(() => { this.stockRows = [] })
        .finally(() => { this.stockLoading = false })
    },
    async dismiss () {
      this.dismissing = true
      try {
        await axios.post(`/harvesting2/parcel/${this.apv}/dismiss`,
          { reason: this.dismissReason })
        this.notify('APV écarté', 'Il ne figure plus dans les listes. Rétablissable à tout moment.', 'success')
        this.$router.push({ name: 'harvesting2-parcels' })
      } catch (e) {
        // Le 409 du serveur porte le nombre d'avize et de réceptions : le
        // relayer tel quel, c'est la seule explication utile.
        this.notify('Impossible d\'écarter cet APV',
          (e.response && e.response.data && e.response.data.message) || e.message, 'danger')
      } finally {
        this.dismissing = false
        this.dismissOpen = false
      }
    },
    async restore () {
      try {
        await axios.post(`/harvesting2/parcel/${this.apv}/restore`)
        this.notify('APV rétabli', 'Il réapparaît dans les listes.', 'success')
        await this.load()
      } catch (e) {
        this.notify('Rétablissement impossible',
          (e.response && e.response.data && e.response.data.message) || e.message, 'danger')
      }
    },
    async removeCost (c) {
      try {
        await axios.delete(`/harvesting2/cost/${c.id}`)
        await this.load()
      } catch (e) {
        this.notify('Suppression impossible',
          (e.response && e.response.data && e.response.data.message) || e.message, 'danger')
      }
    }
  },
  created () {
    this.load()
    this.loadStock()
    // Même source que le sélecteur de fournisseur de l'écran Réception.
    axios.get('/people_name/data')
      .then((r) => { this.people = (r.data || []).map((p) => ({ id: p.value, name: p.label || p.text })) })
      .catch(() => { this.people = [] })
    axios.get('/harvesting2/settings')
      .then((r) => { this.settings = r.data || {} })
      .catch(() => { this.settings = {} })
  }
}
</script>
